/**
 * Chatbot Context - Provide/Inject pattern for proper instance isolation
 *
 * This replaces the global singleton pattern in useGlobalIds.ts with Vue's
 * provide/inject to ensure each chatbot instance has its own isolated context.
 *
 * Usage:
 * - Parent (SiddChatBot.vue): Call `provideChatbotContext(chatbotId, tenantId)`
 * - Children (useChatbot, useChatSession, useTheme): Call `useChatbotContext()`
 */

import {
  ref,
  computed,
  provide,
  inject,
  type InjectionKey,
  type Ref,
} from 'vue'
import { ApiClient } from '@/api/http'

/**
 * Chatbot context interface - contains IDs and scoped ApiClient for child composables
 */
export interface IChatbotContext {
  chatbotId: Ref<string | undefined>
  tenantId: Ref<string | undefined>
  userId: Ref<string | undefined>
  storeKey: Ref<string | undefined>
  apiClient: ApiClient
  setChatbotId: (_id: string) => void
  setTenantId: (_id: string) => void
  setUserId: (_id: string) => void
  setStoreKey: (_key: string) => void
}

/**
 * Injection key for type-safe provide/inject
 */
export const ChatbotContextKey: InjectionKey<IChatbotContext> =
  Symbol('ChatbotContext')

/**
 * Creates and provides a new chatbot context.
 * Call this in the root component (SiddChatBot.vue) to establish context.
 *
 * @param initialChatbotId - Optional initial chatbot ID
 * @param initialTenantId - Optional initial tenant ID
 * @returns The created context (also provided to descendants)
 */
export function provideChatbotContext(
  initialChatbotId?: string,
  initialTenantId?: string,
  initialUserId?: string
): IChatbotContext {
  // Clear any stale context from previous instances (important for HMR and multiple apps)
  currentContext = undefined

  // Create a scoped ApiClient for this chatbot instance.
  // Each instance gets its own client to prevent cross-tenant auth leakage when multiple widgets exist on the same page.
  const scopedApiClient = new ApiClient()

  const chatbotId = ref<string | undefined>(initialChatbotId)
  const tenantId = ref<string | undefined>(initialTenantId)
  const userId = ref<string | undefined>(initialUserId)
  const storeKey = ref<string | undefined>(
    deriveStoreKey(initialChatbotId, initialTenantId, initialUserId)
  )

  const setChatbotId = (id: string) => {
    chatbotId.value = id
    // Do NOT mutate storeKey here — it is fixed at context creation time.
    // Changing storeKey after child components have called useChatbot() causes
    // them to switch to a new, uninitialized Pinia store, breaking isInitialized reactivity
  }

  const setTenantId = (id: string) => {
    tenantId.value = id
  }

  const setUserId = (id: string) => {
    userId.value = id
  }

  const setStoreKey = (key: string) => {
    storeKey.value = key
  }

  const context: IChatbotContext = {
    chatbotId: computed(() => chatbotId.value),
    tenantId: computed(() => tenantId.value),
    userId: computed(() => userId.value),
    storeKey: computed(() => storeKey.value),
    apiClient: scopedApiClient,
    setChatbotId,
    setTenantId,
    setUserId,
    setStoreKey,
  }

  // Store in module-level variable for same-component access
  currentContext = context

  // Provide for descendant components
  provide(ChatbotContextKey, context)

  return context
}

/**
 * Module-level context for the providing component.
 * This allows the same component that calls provideChatbotContext() to also
 * use useChatbotContext() - Vue's inject() only works for descendants.
 */
let currentContext: IChatbotContext | undefined

/**
 * Gets the chatbot context - either from the current provider or via inject.
 * This handles both cases:
 * 1. Same component that called provideChatbotContext() - uses module-level ref
 * 2. Descendant components - uses Vue's inject()
 *
 * @param required - If true, throws error when context is missing (default: true)
 * @returns The context
 * @throws Error if required=true and no context is found
 */
export function useChatbotContext<T extends boolean = true>(
  required: T = true as T
): T extends true ? IChatbotContext : IChatbotContext | undefined {
  // First try inject (for descendant components)
  const injectedContext = inject(ChatbotContextKey, undefined)

  // Use injected context or fall back to module-level context (for same component)
  const context = injectedContext ?? currentContext

  if (required && !context) {
    throw new Error(
      'useChatbotContext() requires provideChatbotContext() to be called first. ' +
        'Ensure SiddChatBot or a parent component has called provideChatbotContext().'
    )
  }

  return context as T extends true
    ? IChatbotContext
    : IChatbotContext | undefined
}

/**
 * Clears the current context. Call this when unmounting the root component
 * to prevent stale context in subsequent instances.
 */
export function clearChatbotContext(): void {
  currentContext = undefined
}

export function deriveStoreKey(
  chatbotId?: string,
  tenantId?: string,
  userId?: string
): string | undefined {
  let baseKey: string | undefined
  if (chatbotId) {
    baseKey = `chatbot-${chatbotId}`
  } else if (tenantId) {
    baseKey = `tenant-${tenantId}`
  }

  if (!baseKey) return undefined

  return userId ? `${baseKey}_user-${userId}` : baseKey
}
